import { ROUTES } from '../routeNames'
import { isAuthenticationFailure, loadAuthenticatedUser } from './authRecovery'
import { resolveAccessRedirect, resolveLegacyBillingRedirect } from './requiredPermission'
import { useSettingsAccess } from '@dashboard/modules/settings/settingsAccess'
import { useAuthStore } from '@dashboard/stores/authStore'
import { useConfigStore } from '@dashboard/stores/configStore'
import { usePermissionStore } from '@dashboard/stores/permissionStore'

import type { RouteMiddleware } from './types'

const auth: RouteMiddleware = async ({ to, toast }) => {
  const normalizedTeamPid = Array.isArray(to.params.teamPid) ? to.params.teamPid[0] : to.params.teamPid
  if (normalizedTeamPid === 'demo') {
    return
  }
  if (normalizedTeamPid && !/^\d+$/.test(normalizedTeamPid)) {
    // Home resolves a real team. Sending this to login while the session is
    // already authenticated bounced back onto a param-less dashboard route.
    return { name: ROUTES.HOME }
  }

  const authStore = useAuthStore()
  const outcome = await loadAuthenticatedUser(() => authStore.getUser())
  if (outcome === 'unauthenticated') {
    const safePath = to.fullPath.startsWith('/') ? to.fullPath : '/'
    const query = safePath !== '/' ? { redirect: safePath } : {}
    return { name: ROUTES.LOGIN, query }
  }
  if (outcome === 'unavailable') {
    toast.add({
      severity: 'warn',
      summary: 'Still reconnecting',
      detail: 'RightMessage did not respond. Your session is still here. Try again in a moment.',
      life: 5000
    })
    return false
  }

  try {
    const { requiredPermission, settingsAccess, settingsScope } = to.meta
    // Permissions and per-project billing are read from the open project's
    // config payload, so load the destination project before deciding.
    const needsProjectContext = typeof requiredPermission === 'string' || settingsAccess === 'perTeamBilling'

    if (needsProjectContext && normalizedTeamPid) {
      const parsedTeamPid = Number.parseInt(normalizedTeamPid, 10)

      if (Number.isFinite(parsedTeamPid)) {
        const configStore = useConfigStore()
        const needsPermissionContextHydration =
          configStore.currentTeamPid !== parsedTeamPid ||
          configStore.currentTeam?.pid !== parsedTeamPid ||
          !configStore.owner

        if (needsPermissionContextHydration) {
          configStore.currentTeamPid = parsedTeamPid
          await configStore.configQuery.refetch()
        }
      }
    }

    const { allows } = useSettingsAccess()
    const settingsAllowed = settingsAccess ? allows(settingsAccess) : true
    const legacyBillingRedirect = resolveLegacyBillingRedirect({
      routeName: to.name,
      perTeamBillingAllowed: settingsAllowed,
      legacyBillingAllowed: !settingsAllowed && allows('legacyBilling'),
      teamPid: to.params.teamPid,
      query: to.query,
      hash: to.hash
    })
    if (legacyBillingRedirect) {
      return legacyBillingRedirect
    }

    const { canAccess } = usePermissionStore()
    const redirect = resolveAccessRedirect({
      requiredPermission,
      canAccess,
      settingsAllowed,
      settingsScope,
      teamPid: to.params.teamPid as string | string[] | undefined
    })

    if (redirect) {
      toast.add({
        severity: 'warn',
        summary: 'Access required',
        detail: `${to.meta.title ?? 'This page'} is not available for this account.`,
        life: 4000
      })
      return redirect
    }
  } catch (error) {
    if (!isAuthenticationFailure(error)) {
      return false
    }
    const safePath = to.fullPath.startsWith('/') ? to.fullPath : '/'
    const query = safePath !== '/' ? { redirect: safePath } : {}
    return { name: ROUTES.LOGIN, query }
  }
  return
}

export default auth
